import React, { type ComponentType, type LazyExoticComponent } from 'react';
import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6';
import { RouteErrorBoundary } from 'components/RouteErrorBoundary';
import { lazyWithReload } from 'utils/lazyWithReload';
import {
  candidateMountPath,
  candidateRouteKeys,
  type CandidateRouteKey,
} from 'candidate-url-registry';

import { Layout } from './Layout';
import { RouteWrapper } from './RouteWrapper';
import { adaptCandidateRoute } from './routeAdapters';
import { candidateRouteMounts } from './routeMounts';
import type { CandidateLazyMount, CandidateMount } from './types';

const lazyCache = new Map<
  CandidateLazyMount['load'],
  LazyExoticComponent<ComponentType>
>();

function lazyPage(load: CandidateLazyMount['load']) {
  const cached = lazyCache.get(load);
  if (cached) {
    return cached;
  }
  const lazy = lazyWithReload(load);
  lazyCache.set(load, lazy);
  return lazy;
}

function renderMount(
  key: CandidateRouteKey,
  mount: CandidateMount
): React.ReactElement {
  const Component = adaptCandidateRoute(
    key,
    mount.kind === 'eager' ? mount.component : lazyPage(mount.load)
  );
  switch (mount.wrapper) {
    case 'route':
      return <RouteWrapper component={Component} />;
    case 'none':
      return <Component />;
    default: {
      const _exhaustive: never = mount.wrapper;
      throw new Error(`Unknown candidate wrapper: ${_exhaustive}`);
    }
  }
}

export function buildCandidateRouteChildren(): RouteObject[] {
  return candidateRouteKeys.map((key) => ({
    // Routes with an optional trailing param mount a different pattern from
    // their navigable template; everywhere else the two are the same.
    path: candidateMountPath(key),
    element: renderMount(key, candidateRouteMounts[key]),
  }));
}

export function createCandidateRouter() {
  return createBrowserRouter([
    {
      path: '/',
      element: <Layout />,
      // Catches route-render failures (including a lazy chunk that failed to
      // load on a stale deploy) so candidates recover/see a fallback instead of
      // React Router's default error UI.
      errorElement: <RouteErrorBoundary />,
      children: buildCandidateRouteChildren(),
    },
  ]);
}
